// src/pages/SmartDocumentEditPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useSmartDocument from "../hooks/useSmartDocument";
import useKnowledgeDB from "../hooks/useKnowledgeDB";
import DatabaseEditModal from "../components/SmartDocument/DatabaseEditModal";
import { showConfirm } from "../modal/confirmModal";
import {
  ArrowLeft,
  FileText,
  Clock,
  Database,
  Send,
  History,
  ChevronDown,
  ChevronUp,
  Download,
  Trash2,
  MessageSquare,
  Sparkles,
  Loader2,
  GitBranch,
  CheckCircle,
} from "lucide-react";
import html2pdf from "html2pdf.js";

const SmartDocumentEditPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNewDocument = sessionId === "new";

  const {
    loading,
    listVersions,
    getSession,
    getVersion,
    listEditHistory,
    listChatMessages,
    createDocument,
    editDocument,
    formatDate,
  } = useSmartDocument();

  const { getDatabases } = useKnowledgeDB();

  // States
  const [selectedSession, setSelectedSession] = useState(null);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [selectedVersionNumber, setSelectedVersionNumber] = useState(0);
  const [sections, setSections] = useState([]);
  const [editHistory, setEditHistory] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [allDatabases, setAllDatabases] = useState([]);

  // UI States
  const [message, setMessage] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [typingAnimation, setTypingAnimation] = useState("");
  const [showDatabaseModal, setShowDatabaseModal] = useState(false);
  const [realSessionId, setRealSessionId] = useState(sessionId);

  const chatEndRef = useRef(null);

  // Handle new document creation
  useEffect(() => {
    if (isNewDocument && location.state?.createData) {
      handleCreateNewDocument();
    } else if (!isNewDocument) {
      setRealSessionId(sessionId);
      loadSessionDetails(sessionId);
    }
  }, [sessionId]);

  // Load databases
  useEffect(() => {
    loadDatabaseList();
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Typing animation
  useEffect(() => {
    if (isProcessing) {
      const messages = [
        "요청을 분석하고 있습니다",
        "지식 데이터베이스를 검색 중입니다",
        "관련 정보를 조합하고 있습니다",
        "문서를 업데이트하고 있습니다",
        "AI가 최적의 답변을 생성 중입니다",
      ];
      let index = 0;
      const interval = setInterval(() => {
        setTypingAnimation(messages[index]);
        index = (index + 1) % messages.length;
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isProcessing]);

  const handleCreateNewDocument = async () => {
    const result = await createDocument(location.state.createData);
    if (result.ok) {
      setRealSessionId(result.sessionId);
      navigate(`/smart-document/${result.sessionId}`, { replace: true });
      loadSessionDetails(result.sessionId);
    } else {
      await showConfirm({
        title: "문서 생성 실패",
        message: result.error || "문서 생성 중 오류가 발생했습니다",
        confirmText: "확인",
        variant: "error",
      });
      navigate("/smart-documents");
    }
  };

  const loadDatabaseList = async () => {
    const result = await getDatabases();
    if (result.ok) {
      setAllDatabases(
        result.databases.filter((db) => db.database_status === "complete")
      );
    }
  };

  const loadSessionDetails = async (sid) => {
    // Load session info
    const sessionResult = await getSession(sid);
    if (sessionResult.ok) {
      setSelectedSession({
        ...sessionResult.session,
        databases: sessionResult.databases || [],
      });
      setDatabases(sessionResult.databases || []);
    }

    // Load versions
    const versionsResult = await listVersions(sid);
    if (versionsResult.ok) {
      setVersions(versionsResult.items);
    }

    // Load current version
    const versionResult = await getVersion(sid, 0);
    if (versionResult.ok) {
      setCurrentVersion(versionResult.version);
      setSections(versionResult.sections);
      setSelectedVersionNumber(versionResult.version.version_number);
    }

    // Load edit history
    const historyResult = await listEditHistory(sid);
    if (historyResult.ok) {
      setEditHistory(historyResult.items);
    }

    // Load chat messages
    const chatResult = await listChatMessages(sid);
    if (chatResult.ok) {
      setChatMessages(chatResult.messages);
    }
  };

  const handleVersionChange = async (versionNumber) => {
    const result = await getVersion(realSessionId, versionNumber);
    if (result.ok) {
      setCurrentVersion(result.version);
      setSections(result.sections);
      setSelectedVersionNumber(versionNumber);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !realSessionId) return;

    const userMessage = message;
    setMessage("");
    setIsProcessing(true);

    setChatMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
        created_at: new Date().toISOString(),
      },
    ]);

    const result = await editDocument(realSessionId, userMessage);
    setIsProcessing(false);

    if (result.ok) {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            result.analysis?.summary || "문서가 성공적으로 수정되었습니다.",
          created_at: new Date().toISOString(),
          newVersion: result.newVersion,
        },
      ]);

      await loadSessionDetails(realSessionId);
    } else {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `처리 중 문제가 발생했습니다: ${result.error}`,
          created_at: new Date().toISOString(),
        },
      ]);
    }
  };

  const handleDownloadPDF = () => {
    if (!currentVersion?.full_html) return;

    const element = document.createElement("div");
    element.innerHTML = currentVersion.full_html;

    const opt = {
      margin: 1,
      filename: `${selectedSession?.template_name}_v${currentVersion?.version_number}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left Sidebar - Version List */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col shadow-sm">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={() => navigate("/smart-documents")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">문서 목록으로</span>
          </button>

          <div className="flex items-center gap-2 mb-3">
            <GitBranch className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">버전 관리</h2>
          </div>

          <div className="text-sm text-gray-600">
            <p className="font-medium">{selectedSession?.template_name}</p>
            <p className="text-xs mt-1">총 {versions.length}개 버전</p>
          </div>
        </div>

        {/* Version List */}
        <div className="flex-1 overflow-y-auto p-3">
          {versions.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <GitBranch className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">버전 정보가 없습니다</p>
            </div>
          ) : (
            <div className="space-y-2">
              {versions.map((version) => (
                <div
                  key={version.id}
                  onClick={() => handleVersionChange(version.version_number)}
                  className={`
                    p-3 rounded-lg border cursor-pointer transition-all
                    ${
                      selectedVersionNumber === version.version_number
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                        ${
                          selectedVersionNumber === version.version_number
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-700"
                        }
                      `}
                      >
                        {version.version_number}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          버전 {version.version_number}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(version.created_at)}
                        </p>
                      </div>
                    </div>
                    {version.version_number ===
                      selectedSession?.current_version && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        최신
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Database Info */}
        {databases.length > 0 && (
          <div className="p-3 border-t border-gray-200">
            <button
              onClick={() => setShowDatabaseModal(true)}
              className="w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">
                    지식 DB ({databases.length})
                  </span>
                </div>
                <span className="text-xs text-gray-500">편집</span>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Header Bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-gray-900">문서 편집</h1>
              <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-full text-sm font-medium">
                현재 버전: {selectedVersionNumber}
              </span>
            </div>
            <button
              onClick={handleDownloadPDF}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              PDF 다운로드
            </button>
          </div>
        </div>

        {/* Content Area - Split View */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left - Chat Interface */}
          <div className="w-1/2 border-r border-gray-200 flex flex-col bg-white">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4">
              {chatMessages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="mb-4 relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full mx-auto flex items-center justify-center">
                        <MessageSquare className="w-10 h-10 text-blue-600" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      AI 문서 편집 도우미
                    </h3>
                    <p className="text-gray-600 mb-4">
                      문서 편집 요청을 입력하면 AI가 자동으로 처리합니다
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${
                        msg.role === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`
                        max-w-[80%] p-4 rounded-2xl
                        ${
                          msg.role === "user"
                            ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
                            : "bg-gray-100 text-gray-900 border border-gray-200"
                        }
                      `}
                      >
                        {msg.role === "assistant" && (
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-purple-600" />
                            <span className="text-xs font-medium text-gray-600">
                              AI Assistant
                            </span>
                          </div>
                        )}
                        <p className="text-sm leading-relaxed">{msg.content}</p>
                        <p
                          className={`text-xs mt-2 ${
                            msg.role === "user"
                              ? "text-blue-100"
                              : "text-gray-500"
                          }`}
                        >
                          {formatDate(msg.created_at)}
                          {msg.newVersion && (
                            <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded">
                              → v{msg.newVersion}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}

                  {isProcessing && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 text-gray-900 p-4 rounded-2xl border border-gray-200 max-w-[80%]">
                        <div className="flex items-center gap-3">
                          <div className="flex space-x-1">
                            <div
                              className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
                              style={{ animationDelay: "0ms" }}
                            ></div>
                            <div
                              className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
                              style={{ animationDelay: "150ms" }}
                            ></div>
                            <div
                              className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
                              style={{ animationDelay: "300ms" }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600">
                            {typingAnimation}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={chatEndRef} />
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex items-end gap-2">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="문서 편집 요청을 입력하세요... (Shift+Enter로 줄바꿈)"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  disabled={isProcessing}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!message.trim() || isProcessing}
                  className="px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg transition-all"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {isProcessing ? "처리중" : "전송"}
                </button>
              </div>
            </div>
          </div>

          {/* Right - Document Artifact */}
          <div className="w-1/2 flex flex-col bg-gray-50">
            {/* Document Viewer */}
            <div className="flex-1 overflow-auto p-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                {currentVersion ? (
                  <div
                    className="p-8 prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: currentVersion.full_html,
                    }}
                  />
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <p>문서를 불러오는 중...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Section Info Bar */}
            {sections.length > 0 && (
              <div className="border-t border-gray-200 bg-white p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-600 flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {sections.length}개 섹션
                    </span>
                    <span className="text-gray-400">|</span>
                    <span className="text-gray-600 flex items-center gap-1">
                      <Sparkles className="w-4 h-4" />
                      신뢰도:{" "}
                      {(
                        (sections.reduce((acc, s) => acc + s.confidence, 0) /
                          sections.length) *
                        100
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Panel - Edit History */}
        {editHistory.length > 0 && (
          <div
            className={`border-t border-gray-200 bg-white transition-all duration-300 shadow-lg ${
              showHistory ? "h-48" : "h-12"
            }`}
          >
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
              onClick={() => setShowHistory(!showHistory)}
            >
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  편집 이력 ({editHistory.length}건)
                </span>
              </div>
              {showHistory ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronUp className="w-4 h-4" />
              )}
            </div>

            {showHistory && (
              <div className="px-4 pb-4 overflow-y-auto max-h-36">
                <div className="space-y-3">
                  {editHistory.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-1 text-gray-500 min-w-[120px] text-xs">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(item.created_at)}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 line-clamp-1">
                          {item.user_request}
                        </p>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                          v{item.from_version} → v{item.to_version}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Database Edit Modal */}
      {showDatabaseModal && (
        <DatabaseEditModal
          currentDatabases={databases}
          allDatabases={allDatabases}
          onClose={() => setShowDatabaseModal(false)}
          onSubmit={(selectedIds) => {
            // TODO: API 연동
            console.log("Update databases:", selectedIds);
            setShowDatabaseModal(false);
          }}
        />
      )}
    </div>
  );
};

export default SmartDocumentEditPage;
